import { useState } from "react";

import { AD_TYPE_OPTIONS, DATE_RANGE_OPTIONS } from "../constants/filters";
import { COPY } from "../copy";
import type { AdsDateRange, InsightsCustomerSegment } from "../types";

import { AdsManagerContent } from "./AdsManagerContent";
import { useAdTypesModal } from "./AdTypesExplainer";
import { CustomersAcquiredTable } from "./customers/CustomersAcquiredTable";
import { InsightsFilterMenu } from "./InsightsFilterMenu";

export function CustomersTab() {
  // Matches the Overview's default so the window doesn't silently change as you
  // move between tabs — a figure that grew because you switched tabs is worse
  // than one scoped tighter than you might want. Lifetime is still a click away
  // for judging a customer over their whole history.
  const [dateRange, setDateRange] = useState<AdsDateRange>("12m");
  const [adType, setAdType] =
    useState<InsightsCustomerSegment>("all-customers");
  const openAdTypesModal = useAdTypesModal();

  return (
    <AdsManagerContent className="py-8 pb-32">
      {/* One flat stack of cards, matching the Overview: the heading sits on the
          page and each block owns its own box. */}
      <div className="space-y-6">
        {/* Both filters scope every card below them, so they sit with the
            heading rather than inside any one card, matching the Overview. */}
        <div className="flex flex-col gap-4 min-[733px]:flex-row min-[733px]:items-start min-[733px]:justify-between">
          <div>
            <h2 className="text-xl leading-8 font-medium text-[#333]">
              {COPY.customers.heading}
            </h2>
            <p className="max-w-[720px] text-sm leading-5 text-[#757575]">
              {COPY.customers.subhead}
            </p>
          </div>
          <div className="flex shrink-0 flex-col gap-2 min-[733px]:flex-row min-[733px]:items-center">
            <InsightsFilterMenu
              name="customers-ad-type"
              labelPrefix="Ad type"
              menuTitle="Filter by ad type"
              value={adType}
              options={AD_TYPE_OPTIONS}
              onChange={setAdType}
              clearValue="all-customers"
              titleInfo={
                openAdTypesModal
                  ? {
                      label: COPY.overview.adTypesModal.title,
                      onClick: openAdTypesModal,
                    }
                  : undefined
              }
            />
            <InsightsFilterMenu
              name="customers-date-range"
              labelPrefix="Date range"
              menuTitle="Filter by date range"
              value={dateRange}
              options={DATE_RANGE_OPTIONS}
              onChange={setDateRange}
              applyToConfirm
            />
          </div>
        </div>

        {/* Study build: the two store-type cards that sat here are out of scope
            for the test, so the tab is the conversions table and its filters.
            CustomerInsightsSection is still on disk, just unrendered. */}
        <CustomersAcquiredTable customerSegment={adType} />
      </div>
    </AdsManagerContent>
  );
}
